<script lang="ts">
    // Handles events for when the app changes, this is right on top because it needs to be high priority
    import "@utils/safe-inset"
    import "@utils/underhood/app-state-change"
    import "@utils/material/expandable"
    import SideMenu from "@components/navigator/SideMenu.svelte"
    import AppShell from "@components/app-shell/AppShell.svelte"
    import StatusBarBackdrop from "@components/app-shell/StatusBarBackdrop.svelte"
    import { SplashScreen } from "@capacitor/splash-screen"
    import BottomPopup from "@components/bottomPopup/bottomPopup.svelte"
    import { userStatus } from "@utils/underhood/firebase"
    import LoadBlocker from "@components/utilities/LoadBlocker.svelte"
    import HighlightExpanderOverlay from "@components/popupSelection/HighlightExpanderOverlay.svelte"
    import "@store/user"
    import { selectTab, tabsReady } from "@store/pageTracker"
    import "@store/highlights"
    import Palette, { paletteOpen } from "@components/highlighting/Palette.svelte"
    import Help from "@components/help/Help.svelte"
    import { blockForUserLoad } from "@utils/helpers/unload-promise"
    import "@utils/practice/notifications-data"
    import OnboardingIntro, {
        onboardingSkipped,
    } from "@components/onboarding/OnboardingIntro.svelte"
    import { installedPWA, isNative } from "@utils/underhood/browser"
    import Install, { installSkipped } from "@components/onboarding/Install.svelte"

    // Necessary to make sure the links in the footnotes work. More info in the file
    import { testRedirectionPath } from "@utils/fallback-book-redirection"
    import { afterNavigate, goto } from "$app/navigation"
    import { page } from "$app/stores"
    import { onMount } from "svelte"

    // Required for loading
    import "@utils/material/darkmode"
    import "@utils/directives/lock-touch-scroll.css"
    import ZoomOnboarding from "@components/zoom-clip-onboarding/ZoomOnboarding.svelte"
    import ContextTextSelectionModule from "@components/highlighting/ContextTextSelectionModule.svelte"
    import QuotedPiece from "@components/popup/modules/QuotedPiece.svelte"

    // Required to receive events from notification actions
    import "@utils/cap-notification-manager/lesson-click-receiver"
    import ConflictLessonPopup from "./practice/ConflictLessonPopup.svelte"
    import Celebrate from "./practice/Celebrate.svelte"
    import ReAuth from "@components/forms/ReAuth.svelte"
    import { setupDeepLinks } from "@utils/deep-links"
    import { screenshotMode } from "@utils/screenshot-mode"

    // Required for cancelling native notifications
    import "@utils/cap-notification-manager/cap-notification-auto-cancel"
    import ImageQuoteEditor from "@components/image-quote-editor/ImageQuoteEditor.svelte"
    import Toasts from "@components/utilities/Toasts.svelte"
    // import WindowOpenTabs from "@components/window/window-open-tabs.svelte"

    // For future optimisations, we can move some work
    // to the ACIM worker
    // import "@worker/acim-worker-wrapper"

    // export let data

    onMount(() => {
        setupDeepLinks()
    })

    /**
     * Shortcut processing (e.g acimce.app/:T-3.I.II -> )
     */
    afterNavigate((nav) => {
        const pathname = nav.to?.url.pathname ?? window.location.pathname
        const redirect = testRedirectionPath(pathname)
        if (redirect) {
            console.log("Short-hand redirection found for: ", pathname)
            if ($tabsReady) selectTab("abandoned")
            goto(redirect)
            return false
        }
        return true
    })

    function isHomepagePath(pathname?: string) {
        const path = (pathname ?? "").replace(/\/+$/, "")
        return path === "" || path === "index.html" || path === "/index.html"
    }

    let homepage: boolean
    $: homepage = isHomepagePath($page.url.pathname) || isHomepagePath(window.location.pathname)

    //
    //  DETERMINING WHICH SCREEN TO GO TO ON START
    //  and also whether to show "desktop" popup
    //

    const onboardingEnabled = installedPWA || isNative
    const installPrompt = !onboardingEnabled
    const defaultOgImagePath = "/images/acimce-cover.webp"

    $: ogUrl = `${$page.url.origin}${$page.url.pathname}`
    $: ogImageUrl = `${$page.url.origin}${defaultOgImagePath}`

    // Unfortunately it's difficult to detect devices reliably because
    // there are so many with so many different configurations. This
    // should have a kind of state-driven design to it because it's
    // possible that someone is using a desktop just on a smaller
    // window for example. And they might expand it.
    //
    // ONBOARDING LOGIC
    //
    // Bad place for this logic but I have to put this here because
    // routify's $page requires we be in the /pages/ subfolder
    // so I can't put it in an outside module

    $: showOnboarding =
        !$screenshotMode &&
        $userStatus == "out" &&
        !$onboardingSkipped &&
        homepage &&
        onboardingEnabled
    $: showInstallPrompt = !$screenshotMode && installPrompt && homepage && !$installSkipped

    new Promise((res) => setTimeout(res, 800)).then(() => SplashScreen.hide())
</script>

<svelte:head>
    <meta property="og:url" content={ogUrl} />
    <meta property="og:image" content={ogImageUrl} />
    <meta property="og:image:type" content="image/webp" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="800" />
    <meta
        property="og:image:alt"
        content="A Course in Miracles book resting beside houseplants on a wooden table"
    />
    <meta name="twitter:image" content={ogImageUrl} />
    <meta
        name="twitter:image:alt"
        content="A Course in Miracles book resting beside houseplants on a wooden table"
    />
</svelte:head>

<LoadBlocker
    buffer={2000}
    loaded={!$blockForUserLoad}
    wrapperID={"main-loader"}
    messages={[
        {
            deltatime: 5000,
            html: "",
        },
        {
            deltatime: 10000,
            html: "🔐<br />We're having trouble reaching the server.<br />You'll still be able to use the app after about 30 seconds.",
        },
        {
            deltatime: 35000,
            html: "🔐<br />.",
        },
    ]}
>
    <BottomPopup />
    <SideMenu />
    {#if $paletteOpen && $page.url.pathname.startsWith("/book/:notation")}
        <Palette />
    {/if}
    <HighlightExpanderOverlay />
    <Celebrate />
    <ReAuth />
    <ImageQuoteEditor />
    <Help />
    <ContextTextSelectionModule />
    {#if !$screenshotMode}
        <ZoomOnboarding />
    {/if}
    <!-- <WindowOpenTabs /> -->
    <Install {showInstallPrompt} />
    {#if showOnboarding}
        <OnboardingIntro />
    {/if}
    {#if !$screenshotMode && $userStatus === "verified"}
        <ConflictLessonPopup />
    {/if}
    <AppShell>
        <slot>
            <!-- pages in this folder and subfolders
          will be rendered here -->
        </slot>
    </AppShell>
    <QuotedPiece />
    <StatusBarBackdrop />

    <!-- Not sure what this is used for -->
    <div id="invisible" style="display:none" />
</LoadBlocker>
<Toasts />

<style>
    :global(#main-loader) {
        position: fixed;
        inset: 0;
        background-color: var(--color-purple-900);
        z-index: var(--z-popup-top-layer);
    }
</style>
